import { apiClient } from "./client";

export const LOOM_LOCAL_DEMO_PROJECT_ID = "00000000-0000-4000-8000-000000009009" as const;
export const LOOM_AUTHORING_ASSISTANT_MODEL = "qwen2.5:3b" as const;

export type LoomAuthoringAssistantConnectionState =
  | "ready"
  | "disabled"
  | "invalid-config"
  | "ollama-unreachable"
  | "model-missing"
  | "invalid-response";

export interface LoomAuthoringAssistantStatus {
  schema: "loom-authoring-assistant-status/v1";
  provider: "local-ollama";
  state: LoomAuthoringAssistantConnectionState;
  connected: boolean;
  model: typeof LOOM_AUTHORING_ASSISTANT_MODEL;
  reason: string | null;
  action: string | null;
}

export type LoomAssistantControlValue = number | boolean | string;

export interface LoomAssistantControlDescriptor {
  id: string;
  label: string;
  unit: string;
  value: LoomAssistantControlValue;
  hardMin?: number;
  hardMax?: number;
  uiMin?: number;
  uiMax?: number;
  allowedValues?: readonly string[];
}

export interface LoomAuthoringAssistantRequest {
  schema: "loom-authoring-assistant-request/v2";
  prompt: string;
  expectedRevision: number;
  selectedNodeId: string;
  controls: readonly LoomAssistantControlDescriptor[];
  observableIds: readonly string[];
}

export interface LoomAuthoringAssistantEdit {
  controlId: string;
  value: LoomAssistantControlValue;
}

export interface LoomAuthoringAssistantProposal {
  schema: "loom-authoring-assistant-proposal/v2";
  model: string;
  expectedRevision: number;
  message: string;
  edits: readonly LoomAuthoringAssistantEdit[];
  focusControlIds: readonly string[];
  goal: LoomAuthoringAssistantGoal | null;
}

export interface LoomAuthoringAssistantGoal {
  controlId: string;
  observableId: string;
  targetValue: number;
  searchMin: number;
  searchMax: number;
  tolerance: number;
  execution: "blocked" | "ready";
  blockedReason?: string;
}

const object = (value: unknown, path: string): Record<string, unknown> => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Record<string, unknown>;
};

const exactKeys = (value: Record<string, unknown>, keys: readonly string[], path: string) => {
  const extras = Object.keys(value).filter((key) => !keys.includes(key));
  if (extras.length) throw new Error(`${path} contains unknown fields: ${extras.join(", ")}`);
};

const CONNECTION_STATES: readonly LoomAuthoringAssistantConnectionState[] = [
  "ready",
  "disabled",
  "invalid-config",
  "ollama-unreachable",
  "model-missing",
  "invalid-response",
];

export function parseLoomAuthoringAssistantStatus(value: unknown): LoomAuthoringAssistantStatus {
  const status = object(value, "status");
  exactKeys(status, ["schema", "provider", "state", "connected", "model", "reason", "action"], "status");
  if (status.schema !== "loom-authoring-assistant-status/v1") throw new Error("Unsupported Loom assistant status schema");
  if (status.provider !== "local-ollama") throw new Error("Loom assistant status provider must be local-ollama");
  if (typeof status.state !== "string" || !CONNECTION_STATES.includes(status.state as LoomAuthoringAssistantConnectionState)) {
    throw new Error("Loom assistant status state is invalid");
  }
  if (typeof status.connected !== "boolean") throw new Error("Loom assistant status connected must be boolean");
  if (status.model !== LOOM_AUTHORING_ASSISTANT_MODEL) throw new Error("Loom assistant status model is not the bounded local model");
  const state = status.state as LoomAuthoringAssistantConnectionState;
  const reason = status.reason;
  const action = status.action;
  if (state === "ready") {
    if (!status.connected || reason !== null || action !== null) throw new Error("Ready Loom assistant status is inconsistent");
  } else if (status.connected || typeof reason !== "string" || !reason.trim()
    || typeof action !== "string" || !action.trim()) {
    throw new Error("Disconnected Loom assistant status is inconsistent");
  }
  return {
    schema: "loom-authoring-assistant-status/v1",
    provider: "local-ollama",
    state,
    connected: status.connected,
    model: LOOM_AUTHORING_ASSISTANT_MODEL,
    reason: reason as string | null,
    action: action as string | null,
  };
}

export async function getLoomAuthoringAssistantStatus(
  projectId: string,
  signal?: AbortSignal,
): Promise<LoomAuthoringAssistantStatus> {
  if (!projectId.trim()) throw new Error("A Thread project id is required for Loom assistant access");
  const response = await apiClient<unknown>(
    `/thread/v1/projects/${encodeURIComponent(projectId)}/aero/loom/authoring/status`,
    { method: "GET", signal },
  );
  return parseLoomAuthoringAssistantStatus(response);
}

function validateRequest(request: LoomAuthoringAssistantRequest): void {
  if (request.schema !== "loom-authoring-assistant-request/v2") throw new Error("Unsupported Loom assistant request schema");
  if (request.prompt !== request.prompt.trim() || request.prompt.length < 1 || request.prompt.length > 2_000) {
    throw new Error("Loom assistant prompt must be a trimmed string between 1 and 2000 characters");
  }
  if (!Number.isSafeInteger(request.expectedRevision) || request.expectedRevision < 0) {
    throw new Error("Loom assistant expectedRevision must be a non-negative safe integer");
  }
  if (!request.selectedNodeId || request.selectedNodeId.length > 128) throw new Error("Loom assistant selectedNodeId is invalid");
  if (request.controls.length < 1 || request.controls.length > 128) throw new Error("Loom assistant needs between 1 and 128 allowed controls");
  if (new Set(request.controls.map((control) => control.id)).size !== request.controls.length) {
    throw new Error("Loom assistant control ids must be unique");
  }
  for (const control of request.controls) {
    if (typeof control.value === "string") {
      if (control.unit !== "enum" || !control.allowedValues?.includes(control.value)) {
        throw new Error(`Loom assistant enum control ${control.id} needs an allowedValues allowlist`);
      }
      if (new Set(control.allowedValues).size !== control.allowedValues.length) {
        throw new Error(`Loom assistant enum control ${control.id} has duplicate allowedValues`);
      }
    } else if (typeof control.value === "boolean") {
      if (control.unit !== "boolean" || control.allowedValues !== undefined) {
        throw new Error(`Loom assistant boolean control ${control.id} has an invalid domain`);
      }
    } else {
      if (!Number.isFinite(control.value) || control.allowedValues !== undefined) {
        throw new Error(`Loom assistant numeric control ${control.id} has an invalid domain`);
      }
      if (!Number.isFinite(control.uiMin) || !Number.isFinite(control.uiMax) || control.uiMin! >= control.uiMax!) {
        throw new Error(`Loom assistant numeric control ${control.id} needs finite ordered UI bounds`);
      }
      if (control.value < control.uiMin! || control.value > control.uiMax!) {
        throw new Error(`Loom assistant numeric control ${control.id} is outside its UI bounds`);
      }
      if (control.hardMin !== undefined && control.value < control.hardMin) {
        throw new Error(`Loom assistant numeric control ${control.id} is below its hard minimum`);
      }
      if (control.hardMax !== undefined && control.value > control.hardMax) {
        throw new Error(`Loom assistant numeric control ${control.id} exceeds its hard maximum`);
      }
      if (control.unit === "count" && !Number.isInteger(control.value)) {
        throw new Error(`Loom assistant count control ${control.id} must be an integer`);
      }
    }
  }
  if (request.observableIds.length < 1 || request.observableIds.length > 8) {
    throw new Error("Loom assistant needs between 1 and 8 observable ids");
  }
  if (new Set(request.observableIds).size !== request.observableIds.length) {
    throw new Error("Loom assistant observable ids must be unique");
  }
  if (request.observableIds.some((id) => !id || /\s/.test(id))) {
    throw new Error("Loom assistant observable ids must be stable non-empty ids");
  }
}

function finite(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`${path} must be finite`);
  return value;
}

export function parseLoomAuthoringAssistantProposal(
  value: unknown,
  request: LoomAuthoringAssistantRequest,
): LoomAuthoringAssistantProposal {
  validateRequest(request);
  const proposal = object(value, "proposal");
  exactKeys(proposal, ["schema", "model", "expectedRevision", "message", "edits", "focusControlIds", "goal"], "proposal");
  if (proposal.schema !== "loom-authoring-assistant-proposal/v2") throw new Error("Unsupported Loom assistant proposal schema");
  if (typeof proposal.model !== "string" || proposal.model.trim() === "") throw new Error("proposal.model must be a non-empty string");
  if (proposal.expectedRevision !== request.expectedRevision) throw new Error("The Loom assistant proposal is stale");
  if (typeof proposal.message !== "string" || proposal.message.trim() !== proposal.message || proposal.message.length < 1 || proposal.message.length > 1_200) {
    throw new Error("proposal.message is invalid");
  }
  if (!Array.isArray(proposal.edits) || proposal.edits.length > 32) throw new Error("proposal.edits must contain at most 32 edits");
  const allowed = new Map(request.controls.map((control) => [control.id, control]));
  const used = new Set<string>();
  const edits = proposal.edits.map((raw, index): LoomAuthoringAssistantEdit => {
    const edit = object(raw, `proposal.edits[${index}]`);
    exactKeys(edit, ["controlId", "value"], `proposal.edits[${index}]`);
    if (typeof edit.controlId !== "string" || used.has(edit.controlId)) throw new Error("proposal edits need unique controlId values");
    const control = allowed.get(edit.controlId);
    if (!control) throw new Error(`proposal edit ${edit.controlId} is not an allowed control`);
    used.add(edit.controlId);
    const value = edit.value;
    if (typeof value !== typeof control.value || !["number", "boolean", "string"].includes(typeof value)) {
      throw new Error(`proposal edit ${edit.controlId} has the wrong value type`);
    }
    if (typeof value === "number") {
      if (!Number.isFinite(value)) throw new Error(`proposal edit ${edit.controlId} must be finite`);
      if (control.hardMin !== undefined && value < control.hardMin) throw new Error(`proposal edit ${edit.controlId} is below its hard minimum`);
      if (control.hardMax !== undefined && value > control.hardMax) throw new Error(`proposal edit ${edit.controlId} exceeds its hard maximum`);
    } else if (typeof value === "string" && !control.allowedValues?.includes(value)) {
      throw new Error(`proposal edit ${edit.controlId} is not an allowed enum value`);
    }
    return { controlId: edit.controlId, value: value as LoomAssistantControlValue };
  });
  if (!Array.isArray(proposal.focusControlIds) || proposal.focusControlIds.length > 4) {
    throw new Error("proposal.focusControlIds must contain at most 4 ids");
  }
  const focusControlIds = proposal.focusControlIds.map((controlId) => {
    if (typeof controlId !== "string" || !allowed.has(controlId)) {
      throw new Error(`proposal focus ${String(controlId)} is not an allowed control`);
    }
    return controlId;
  });
  if (new Set(focusControlIds).size !== focusControlIds.length) {
    throw new Error("proposal.focusControlIds must be unique");
  }
  let goal: LoomAuthoringAssistantGoal | null = null;
  if (proposal.goal !== null) {
    const rawGoal = object(proposal.goal, "proposal.goal");
    exactKeys(rawGoal, ["controlId", "observableId", "targetValue", "searchMin", "searchMax", "tolerance", "execution", "blockedReason"], "proposal.goal");
    if (typeof rawGoal.controlId !== "string") throw new Error("proposal.goal.controlId is invalid");
    const control = allowed.get(rawGoal.controlId);
    if (!control || typeof control.value !== "number" || control.unit === "count") {
      throw new Error("proposal goal control must be an allowed continuous numeric control");
    }
    if (typeof rawGoal.observableId !== "string" || !request.observableIds.includes(rawGoal.observableId)) {
      throw new Error("proposal goal observable is not requested");
    }
    const targetValue = finite(rawGoal.targetValue, "proposal.goal.targetValue");
    const searchMin = finite(rawGoal.searchMin, "proposal.goal.searchMin");
    const searchMax = finite(rawGoal.searchMax, "proposal.goal.searchMax");
    const tolerance = finite(rawGoal.tolerance, "proposal.goal.tolerance");
    if (searchMin >= searchMax || searchMin !== control.uiMin || searchMax !== control.uiMax) {
      throw new Error("proposal goal search bounds do not match the control UI bounds");
    }
    if (tolerance <= 0) throw new Error("proposal goal tolerance must be positive");
    if (rawGoal.execution !== "blocked" && rawGoal.execution !== "ready") {
      throw new Error("proposal goal execution is invalid");
    }
    const blockedReason = rawGoal.blockedReason;
    if (rawGoal.execution === "blocked" && (typeof blockedReason !== "string" || blockedReason.trim() === "")) {
      throw new Error("blocked proposal goal requires blockedReason");
    }
    if (rawGoal.execution === "ready" && blockedReason !== undefined) {
      throw new Error("ready proposal goal cannot include blockedReason");
    }
    goal = {
      controlId: rawGoal.controlId,
      observableId: rawGoal.observableId,
      targetValue,
      searchMin,
      searchMax,
      tolerance,
      execution: rawGoal.execution,
      ...(blockedReason === undefined ? {} : { blockedReason: blockedReason as string }),
    };
  }
  const active = Number(edits.length > 0) + Number(focusControlIds.length > 0) + Number(goal !== null);
  if (active > 1) throw new Error("proposal may populate only one action channel");
  return {
    schema: "loom-authoring-assistant-proposal/v2",
    model: proposal.model,
    expectedRevision: request.expectedRevision,
    message: proposal.message,
    edits,
    focusControlIds,
    goal,
  };
}

export async function proposeLoomAuthoringEdits(
  projectId: string,
  request: LoomAuthoringAssistantRequest,
  signal?: AbortSignal,
): Promise<LoomAuthoringAssistantProposal> {
  validateRequest(request);
  if (!projectId.trim()) throw new Error("A Thread project id is required for Loom assistant access");
  const response = await apiClient<unknown>(
    `/thread/v1/projects/${encodeURIComponent(projectId)}/aero/loom/authoring/proposals`,
    { method: "POST", body: request, signal },
  );
  return parseLoomAuthoringAssistantProposal(response, request);
}
